import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { DateHelper } from "@bryntum/schedulerpro";
import { format, parseISO } from "date-fns";
import { CalendarPlus, Hourglass, MessageSquare, Paperclip, RefreshCw, UserRound } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/modern/components/ui/tabs";
import { cn } from "@/modern/utils";
import { getAging } from "@/other/utils/utils";
import type { CheckGridCellHistoryEntry, CheckGridDefinition, CheckGridMatrixCell, CheckgridEvidence } from "./types";
import { CheckgridPanelFrame, EvidenceViewer } from "./CheckgridPanelFrame";
import { DISPLAY_STATE_LABEL, cellStatusLabel, historyAction, statusDotClass } from "./utils/display";

export type CheckgridReviewEntry = {
  key: string;
  label: string;
  itemId: number;
  itemLabel: string;
  cell: CheckGridMatrixCell | undefined;
  /** Item-level comment (not period state). */
  comment?: string | null;
  evidence: CheckgridEvidence | null;
  loadHistory?: () => Promise<CheckGridCellHistoryEntry[]>;
};

export type CheckgridReviewGroup = {
  id: number;
  label: string;
  entries: CheckgridReviewEntry[];
};

const STATUS_ORDER = [
  DISPLAY_STATE_LABEL.satisfied,
  cellStatusLabel("incomplete", "attention_needed"),
  DISPLAY_STATE_LABEL.missed,
  DISPLAY_STATE_LABEL.incomplete,
  DISPLAY_STATE_LABEL.expected,
  DISPLAY_STATE_LABEL.not_applicable
];

type Props = {
  gridName: string;
  dayLabel: string;
  groups: CheckgridReviewGroup[];
  definition?: CheckGridDefinition | null;
  onClose: () => void;
};

function stamp(iso: string | null | undefined, by: string | null | undefined): string | null {
  if (!iso) return null;
  const when = format(parseISO(iso), "d MMM yyyy, h:mm a");
  return by ? `${by} · ${when}` : when;
}

function isDone(cell: CheckGridMatrixCell | undefined) {
  return cell?.display_state === "satisfied";
}

function EmptyNote({ children }: { children: string }) {
  return <p className="px-1.5 text-[12.5px] text-muted-foreground">{children}</p>;
}

function HistoryList({ entries }: { entries: CheckgridReviewEntry[] }) {
  const withHistory = entries.filter((entry) => entry.loadHistory);
  const results = useQueries({
    queries: withHistory.map((entry) => ({
      queryKey: ["checkgrid-item-history", entry.key, entry.cell?.checklist_item_id, entry.cell?.recorded_at, entry.cell?.display_state],
      queryFn: entry.loadHistory as () => Promise<CheckGridCellHistoryEntry[]>
    }))
  });

  if (results.some((result) => result.isLoading)) return <EmptyNote>Loading history…</EmptyNote>;
  if (results.length > 0 && results.every((result) => result.isError)) return <p className="px-1.5 text-[12.5px] text-destructive">Unable to load history.</p>;

  const actions = withHistory
    .flatMap((entry, index) => {
      const ordered = [...(results[index]?.data ?? [])].sort((a, b) => a.recorded_at.localeCompare(b.recorded_at));
      return ordered.flatMap((row, rowIndex) => {
        const action = historyAction(row, ordered[rowIndex - 1]);
        return action ? [{ id: `${entry.key}:${row.id}`, at: row.recorded_at, item: entry.itemLabel, ...action }] : [];
      });
    })
    .sort((a, b) => b.at.localeCompare(a.at));

  if (!actions.length) return <EmptyNote>No history yet.</EmptyNote>;
  return (
    <ol className="space-y-3.5 px-1.5">
      {actions.map(({ id, at, item, label, by, comment, state }) => (
        <li key={id} className="flex items-start gap-3">
          <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", state ? statusDotClass(state) : "bg-muted-foreground/60")} aria-hidden />
          <div className="min-w-0 text-[12.5px]">
            <p className="text-[13.5px] leading-snug text-foreground">{label}</p>
            <p className="mt-0.5 text-muted-foreground">{by ? `${item} by ${by}` : item}</p>
            <p className="text-muted-foreground">{format(parseISO(at), "d MMM yyyy, h:mm a")}</p>
            {comment ? <p className="mt-1 whitespace-pre-wrap text-foreground/90">{comment}</p> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function CheckgridReviewPanel({ gridName, dayLabel, groups, definition, onClose }: Props) {
  const [viewer, setViewer] = useState<{ src: string; alt: string } | null>(null);

  const entries: CheckgridReviewEntry[] = groups.flatMap((g) => g.entries);
  const cells = entries.map((e) => e.cell);
  const breakdown = new Map<string, { count: number; dot: string }>();
  for (const cell of cells) {
    const state = cell?.display_state ?? "incomplete";
    const label = cellStatusLabel(state, cell?.attention);
    const current = breakdown.get(label);
    breakdown.set(label, { count: (current?.count ?? 0) + 1, dot: statusDotClass(state, cell?.attention) });
  }
  const statusCounts = [...breakdown.entries()].sort(([a], [b]) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b));

  const doneBy = new Map<string, number>();
  for (const cell of cells) {
    if (isDone(cell) && cell?.checked_by) doneBy.set(cell.checked_by, (doneBy.get(cell.checked_by) ?? 0) + 1);
  }
  // Comment and photo belong to the check, not the person — show each once however many people are selected.
  const itemEntries = [...new Map(entries.map((entry) => [entry.itemId, entry])).values()];
  const comments = itemEntries.flatMap((entry) => {
    const text = (entry.comment ?? entry.cell?.comment)?.trim();
    return text ? [{ entry, text }] : [];
  });
  const assets = itemEntries.filter((entry): entry is CheckgridReviewEntry & { evidence: CheckgridEvidence } => entry.evidence != null);
  const facts = doneBy.size > 0 && {
    icon: UserRound,
    text: `Done by ${[...doneBy.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => (doneBy.size > 1 || count > 1 ? `${name} (${count})` : name))
      .join(", ")}`
  };

  const metadata = [
    { label: "Last updated", icon: RefreshCw, value: stamp(definition?.updated_at, definition?.updater_name) },
    { label: "Created", icon: CalendarPlus, value: stamp(definition?.created_at, definition?.creator_name) },
    { label: "Age", icon: Hourglass, value: definition?.created_at ? DateHelper.formatCount(getAging(definition.created_at), "day") : null }
  ].filter((row): row is { label: string; icon: typeof RefreshCw; value: string } => row.value != null);

  return (
    <CheckgridPanelFrame gridName={gridName} dayLabel={dayLabel} onClose={onClose}>
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-5">
        <div className="px-1.5">
          <h2 className="text-[17px] leading-snug font-medium tracking-tight text-foreground">{entries.length} checks selected</h2>
          <div className="mt-3 flex h-1 overflow-hidden rounded-full bg-border/60" aria-hidden>
            {statusCounts.map(([label, { count, dot }]) => (
              <span key={label} className={cn("h-full transition-[width] duration-300 motion-reduce:transition-none", dot)} style={{ width: `${(count / entries.length) * 100}%` }} />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
            {statusCounts.map(([label, { count, dot }]) => (
              <span key={label} className="flex items-center gap-1.5">
                <span className={cn("size-2 shrink-0 rounded-full", dot)} aria-hidden />
                <span className="text-foreground tabular-nums">{count}</span>
                {label}
              </span>
            ))}
          </div>
          {metadata.length > 0 && (
            <dl className="mt-4 divide-y divide-border/40 overflow-hidden rounded-lg border border-border/50 bg-muted/20 text-[12.5px]">
              {metadata.map(({ label, icon: Icon, value }) => (
                <div key={label} className="flex items-center justify-between gap-3 px-3 py-2">
                  <dt className="flex shrink-0 items-center gap-2 text-muted-foreground">
                    <Icon className="size-3.5 opacity-70" aria-hidden />
                    {label}
                  </dt>
                  <dd className="min-w-0 truncate text-right text-foreground tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
          )}
          {facts && (
            <p className="mt-3.5 flex items-start gap-2 text-[12.5px] text-muted-foreground">
              <facts.icon className="mt-0.5 size-3.5 shrink-0 opacity-70" aria-hidden />
              <span>{facts.text}</span>
            </p>
          )}
        </div>

        <Tabs defaultValue="history" className="flex flex-col gap-4">
          <TabsList className="h-9 w-full">
            <TabsTrigger value="history" className="flex-1 text-[13px]">
              History
            </TabsTrigger>
            <TabsTrigger value="assets" className="flex-1 gap-1.5 text-[13px]">
              Assets
              {assets.length > 0 && <span className="text-muted-foreground tabular-nums">{assets.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="comments" className="flex-1 gap-1.5 text-[13px]">
              Comments
              {comments.length > 0 && <span className="text-muted-foreground tabular-nums">{comments.length}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="mt-0 focus-visible:outline-none">
            <HistoryList entries={entries} />
          </TabsContent>

          <TabsContent value="assets" className="mt-0 focus-visible:outline-none">
            {assets.length ? (
              <ul className="grid grid-cols-2 gap-3 px-1.5">
                {assets.map((entry) => (
                  <li key={entry.itemId} className="min-w-0">
                    <button
                      type="button"
                      onClick={() => setViewer({ src: entry.evidence.full, alt: entry.itemLabel })}
                      className="block w-full overflow-hidden rounded-md border border-border/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <img src={entry.evidence.thumbnail} alt="" className="h-24 w-full object-cover" />
                    </button>
                    <p className="mt-1 flex items-center gap-1 truncate text-[12px] text-muted-foreground">
                      <Paperclip className="size-3 shrink-0 opacity-70" aria-hidden />
                      <span className="truncate">{entry.itemLabel}</span>
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyNote>No assets attached.</EmptyNote>
            )}
          </TabsContent>

          <TabsContent value="comments" className="mt-0 focus-visible:outline-none">
            {comments.length ? (
              <ul className="space-y-3.5 px-1.5">
                {comments.map(({ entry, text }) => (
                  <li key={entry.itemId} className="flex items-start gap-3">
                    <MessageSquare className="mt-0.5 size-3.5 shrink-0 text-muted-foreground opacity-70" aria-hidden />
                    <div className="min-w-0 text-[12.5px]">
                      <p className="truncate text-muted-foreground">{entry.itemLabel}</p>
                      <p className="mt-0.5 whitespace-pre-wrap text-[13.5px] text-foreground">{text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyNote>No comments yet.</EmptyNote>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <EvidenceViewer src={viewer?.src ?? null} alt={viewer?.alt ?? ""} onClose={() => setViewer(null)} />
    </CheckgridPanelFrame>
  );
}
